import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';

export default function HomePage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <span className="text-xl font-medium tracking-tight font-serif">Decided</span>
          {user ? (
            <Link to="/app">
              <Button variant="ghost" size="sm" data-testid="header-app-btn">
                My decisions
              </Button>
            </Link>
          ) : (
            <Link to="/login">
              <Button variant="ghost" size="sm" data-testid="header-login-btn">
                Log in
              </Button>
            </Link>
          )}
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="max-w-3xl mx-auto px-6 py-24 md:py-32">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-tight font-serif text-foreground">
            Not a task list.<br />A decision memory.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl">
            Decided helps you capture decisions, follow through on what matters, and remember why it was done.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            {user ? (
              <Link to="/app">
                <Button size="lg" className="rounded-sm px-8" data-testid="hero-app-btn">
                  Go to my decisions
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/register">
                  <Button size="lg" className="rounded-sm px-8" data-testid="hero-cta-btn">
                    Start using Decided
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" size="lg" className="rounded-sm px-8" data-testid="hero-login-btn">
                    Log in
                  </Button>
                </Link>
              </>
            )}
          </div>
        </section>

        {/* Problem Section */}
        <section className="border-t border-border">
          <div className="max-w-3xl mx-auto px-6 py-20 md:py-24">
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight font-serif text-foreground">
              Decisions disappear.
            </h2>
            <div className="mt-6 text-muted-foreground leading-relaxed space-y-4">
              <p>
                Decisions are made in meetings, calls, or quick conversations.<br />
                Then they scatter across notes, emails, and task lists.
              </p>
              <p>
                What gets lost isn't the work.<br />
                It's the decision behind it.
              </p>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="border-t border-border bg-secondary/30">
          <div className="max-w-3xl mx-auto px-6 py-20 md:py-24">
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight font-serif text-foreground">
              Decisions, intact.
            </h2>
            <ul className="mt-8 space-y-3 text-muted-foreground">
              <li className="flex items-start gap-3">
                <span className="text-foreground/30 select-none">—</span>
                <span>Capture the decision</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-foreground/30 select-none">—</span>
                <span>Add the follow-ups that make it real</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-foreground/30 select-none">—</span>
                <span>Get reminded at the right time</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-foreground/30 select-none">—</span>
                <span>Close the loop</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Differentiation Section */}
        <section className="border-t border-border">
          <div className="max-w-3xl mx-auto px-6 py-20 md:py-24">
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight font-serif text-foreground">
              Designed for thinking, not busywork.
            </h2>
            <ul className="mt-8 space-y-3 text-muted-foreground">
              <li className="flex items-start gap-3">
                <span className="text-foreground/30 select-none">—</span>
                <span>Decisions first, not tasks</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-foreground/30 select-none">—</span>
                <span>Follow-ups tied to context</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-foreground/30 select-none">—</span>
                <span>Calm, minimal interface</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-foreground/30 select-none">—</span>
                <span>No dashboards. No noise.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Decision Framing Section */}
        <section className="border-t border-border bg-secondary/30">
          <div className="max-w-3xl mx-auto px-6 py-20 md:py-24">
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight font-serif text-foreground">
              Not all decisions are equal.
            </h2>
            <div className="mt-6 text-muted-foreground leading-relaxed space-y-4">
              <p>
                Label decisions as strategic, operational, financial, or personal.
              </p>
              <p>
                A small pause that changes how you think before you act.
              </p>
            </div>
          </div>
        </section>

        {/* Audience Section */}
        <section className="border-t border-border">
          <div className="max-w-3xl mx-auto px-6 py-20 md:py-24">
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight font-serif text-foreground">
              For people who decide every day.
            </h2>
            <div className="mt-6 text-muted-foreground leading-relaxed space-y-4">
              <p>
                Managers, consultants, founders, and operators use Decided to keep important decisions from slipping through the cracks.
              </p>
              <p>
                If your work involves judgment, Decided fits.
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="border-t border-border bg-secondary/30">
          <div className="max-w-3xl mx-auto px-6 py-24 md:py-32 text-center">
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight font-serif text-foreground">
              Remember what you decided.
            </h2>
            <div className="mt-10">
              <Link to="/register">
                <Button size="lg" className="rounded-sm px-10" data-testid="final-cta-btn">
                  Start using Decided
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-3xl mx-auto px-6 py-8">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <Link to="/pricing" className="hover:text-foreground transition-colors">Pricing</Link>
            <Link to="/why" className="hover:text-foreground transition-colors">Why Decided</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            <Link to="/login" className="hover:text-foreground transition-colors">Login</Link>
          </div>
          <p className="mt-6 text-center text-xs text-muted-foreground/50">
            © {new Date().getFullYear()} Decided
          </p>
        </div>
      </footer>
    </div>
  );
}
